import React, { useState, useEffect } from "react";
import { useMiniERP } from "./hooks/useMiniERP";
import { useStatusBanner, StatusBanner } from "./components/MiniERPStatusBanner";
import { generateVoucherPDF } from "./components/MiniERPPdf";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faPlus,
  faEdit,
  faTrash,
  faSave,
  faSearch,
  faTimes,
  faToggleOn,
  faToggleOff,
  faFilePdf,
} from "@fortawesome/free-solid-svg-icons";

const voucherTypes = [
  "Receipt",
  "Payment",
  "Journal",
  "Contra",
  "DebitNote",
  "CreditNote",
];

const emptyEntry = () => ({ ledgerId: "", debit: 0, credit: 0 });

const MiniERPVoucher = ({ authState }) => {
  const {
    fetchVouchers,
    createVoucher,
    updateVoucher,
    toggleVoucher,
    fetchLedgers,
  } = useMiniERP();

  const [vouchersList, setVouchersList] = useState([]);
  const [ledgersList, setLedgersList] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedType, setSelectedType] = useState("All");
  const [showModal, setShowModal] = useState(false);
  const [editingVoucher, setEditingVoucher] = useState(null);

  const { status, showSuccess, showError } = useStatusBanner();
  const [listStatus, setListStatus] = useState(null);

  const handleDownloadPdf = (voucher) => {
    try {
      generateVoucherPDF(voucher);
    } catch (err) {
      setListStatus({ type: 'error', message: 'PDF generation failed: ' + err.message });
    }
  };

  const [formData, setFormData] = useState({
    voucherType: "Journal",
    voucherNumber: "",
    date: new Date().toISOString().split("T")[0],
    narration: "",
    entries: [emptyEntry()],
  });

  const loadVouchers = async () => {
    const res = await fetchVouchers();
    setVouchersList(res?.data || []);
  };

  useEffect(() => {
    const load = async () => {
      const [vRes, lRes] = await Promise.all([fetchVouchers(), fetchLedgers()]);
      setVouchersList(vRes?.data || []);
      setLedgersList(lRes?.data || []);
    };
    load();
  }, []);

  const filteredVouchers = vouchersList.filter((v) => {
    const matchesSearch =
      v.voucherNumber?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      v.narration?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesType =
      selectedType === "All" || v.voucherType === selectedType;
    return matchesSearch && matchesType;
  });

  const addEntryRow = () => {
    setFormData((prev) => ({
      ...prev,
      entries: [...prev.entries, emptyEntry()],
    }));
  };

  const removeEntryRow = (index) => {
    if (formData.entries.length === 1) return;
    setFormData((prev) => ({
      ...prev,
      entries: prev.entries.filter((_, i) => i !== index),
    }));
  };

  const updateEntry = (index, field, value) => {
    const newEntries = [...formData.entries];
    newEntries[index][field] =
      field === "ledgerId" ? value : Number(value) || 0;
    setFormData((prev) => ({ ...prev, entries: newEntries }));
  };

  const calculateTotals = () => {
    const totalDebit = formData.entries.reduce(
      (sum, e) => sum + (e.debit || 0),
      0,
    );
    const totalCredit = formData.entries.reduce(
      (sum, e) => sum + (e.credit || 0),
      0,
    );
    return { totalDebit, totalCredit };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const { totalDebit, totalCredit } = calculateTotals();

    if (Math.abs(totalDebit - totalCredit) > 0.01) {
      showError("Debit and Credit totals must be equal!");
      return;
    }

    const validEntries = formData.entries.filter((en) => en.ledgerId);
    if (validEntries.length === 0) {
      showError("Add at least one ledger entry.");
      return;
    }

    try {
      const payload = {
        voucherType: formData.voucherType,
        voucherNumber:
          formData.voucherNumber || `VCH-${Date.now().toString().slice(-6)}`,
        date: formData.date,
        narration: formData.narration,
        entries: validEntries,
      };

      if (editingVoucher) {
        await updateVoucher(editingVoucher._id, payload);
        showSuccess("Voucher updated successfully!");
      } else {
        await createVoucher(payload);
        showSuccess("Voucher created successfully!");
      }

      await loadVouchers();
      setTimeout(() => { setShowModal(false); resetForm(); }, 700);
    } catch (err) {
      showError(err.message);
    }
  };

  const handleToggle = async (id) => {
    try {
      await toggleVoucher(id);
      await loadVouchers();
      setListStatus({ type: 'success', message: 'Status updated.' });
      setTimeout(() => setListStatus(null), 2500);
    } catch (err) {
      setListStatus({ type: 'error', message: 'Toggle failed: ' + err.message });
    }
  };

  const openCreateModal = () => {
    resetForm();
    setShowModal(true);
  };

  const openEditModal = (voucher) => {
    setEditingVoucher(voucher);
    setFormData({
      voucherType: voucher.voucherType,
      voucherNumber: voucher.voucherNumber,
      date: new Date(voucher.date).toISOString().split("T")[0],
      narration: voucher.narration || "",
      entries: voucher.entries.map((en) => ({
        ledgerId: en.ledgerId?._id || en.ledgerId,
        debit: en.debit || 0,
        credit: en.credit || 0,
      })),
    });
    setShowModal(true);
  };

  const resetForm = () => {
    setFormData({
      voucherType: "Journal",
      voucherNumber: "",
      date: new Date().toISOString().split("T")[0],
      narration: "",
      entries: [emptyEntry()],
    });
    setEditingVoucher(null);
  };

  const { totalDebit, totalCredit } = calculateTotals();

  return (
    <div className="min-h-screen bg-gray-900 text-gray-200 p-4 md:p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8">
          <div>
            <h1 className="text-3xl font-bold text-cyan-200">Voucher Entry</h1>
            <p className="text-gray-400">
              Double Entry System • Receipt, Payment, Journal, etc.
            </p>
          </div>
          <button
            onClick={openCreateModal}
            className="mt-4 md:mt-0 bg-cyan-600 hover:bg-cyan-500 w-fit px-2 py-1 rounded-2xl flex items-center gap-2 font-medium"
          >
            <FontAwesomeIcon icon={faPlus} /> New Voucher
          </button>
        </div>

        {/* Filters */}
        <div className="bg-gray-800 rounded-2xl p-4 mb-6 flex flex-col md:flex-row gap-4">
          <div className="flex-1 relative">
            <FontAwesomeIcon
              icon={faSearch}
              className="absolute left-4 top-3.5 text-gray-500"
            />
            <input
              type="text"
              placeholder="Search voucher number or narration..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full bg-gray-700 border border-gray-600 pl-11 py-3 rounded-xl"
            />
          </div>

          <select
            value={selectedType}
            onChange={(e) => setSelectedType(e.target.value)}
            className="bg-gray-700 border border-gray-600 px-2 py-1 rounded-xl"
          >
            <option value="All">All Types</option>
            {voucherTypes.map((type) => (
              <option key={type} value={type}>
                {type}
              </option>
            ))}
          </select>
        </div>

        {listStatus && <div className="mb-4"><StatusBanner status={listStatus} /></div>}

        {/* Vouchers List */}
        <div className="bg-gray-800 rounded-3xl overflow-hidden mb-8">
          {/* ================= MOBILE CARDS ================= */}
          <div className="block lg:hidden space-y-3 p-3">
            {filteredVouchers.map((voucher) => (
              <div
                key={voucher._id}
                className={`bg-gray-800 border border-gray-700 rounded-xl p-3 text-xs ${!voucher.isActive ? "opacity-50" : ""}`}
              >
                <div className="flex justify-between items-start">
                  <div>
                    <div className="font-mono text-cyan-300">{voucher.voucherNumber}</div>
                    <div className="text-sm font-semibold text-white">
                      {new Date(voucher.date).toLocaleDateString()}
                    </div>
                  </div>
                  <span className="px-2 py-1 bg-gray-700 rounded-full text-[11px]">
                    {voucher.voucherType}
                  </span>
                </div>

                {voucher.narration && (
                  <div className="text-gray-400 mt-2 truncate">{voucher.narration}</div>
                )}

                <div className="flex justify-between items-center mt-3">
                  <div className="font-bold text-white">₹{voucher.totalDebit || 0}</div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => handleDownloadPdf(voucher)}
                      className="w-fit px-1 py-1 text-lg text-gray-400 hover:text-cyan-400"
                      title="Download PDF"
                    >
                      <FontAwesomeIcon icon={faFilePdf} />
                    </button>
                    <button
                      onClick={() => handleToggle(voucher._id)}
                      className="w-fit px-1 py-1 text-lg"
                      title={voucher.isActive ? "Cancel voucher" : "Reactivate voucher"}
                    >
                      <FontAwesomeIcon
                        icon={voucher.isActive ? faToggleOn : faToggleOff}
                        className={voucher.isActive ? "text-emerald-400" : "text-gray-500"}
                      />
                    </button>
                    <button
                      onClick={() => openEditModal(voucher)}
                      className="w-fit px-1 py-1 text-lg text-gray-400 hover:text-cyan-400"
                    >
                      <FontAwesomeIcon icon={faEdit} />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* ================= DESKTOP TABLE ================= */}
          <div className="hidden lg:block overflow-x-auto">
            <table className="w-full text-xs lg:text-sm">
              <thead>
                <tr className="border-b border-gray-700">
                  <th className="text-left py-4 px-6">Date</th>
                  <th className="text-left py-4 px-6">Voucher No</th>
                  <th className="text-left py-4 px-6">Type</th>
                  <th className="text-left py-4 px-6">Narration</th>
                  <th className="text-right py-4 px-6">Amount</th>
                  <th className="text-center py-4 px-6">Status</th>
                  <th className="text-center py-4 px-6">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-700">
                {filteredVouchers.map((voucher) => (
                  <tr
                    key={voucher._id}
                    className={`hover:bg-gray-700/50 ${!voucher.isActive ? "opacity-50" : ""}`}
                  >
                    <td className="py-4 px-6">
                      {new Date(voucher.date).toLocaleDateString()}
                    </td>
                    <td className="py-4 px-6 font-mono text-cyan-300">
                      {voucher.voucherNumber}
                    </td>
                    <td className="py-4 px-6">
                      <span className="px-3 py-1 bg-gray-700 rounded-full text-sm">
                        {voucher.voucherType}
                      </span>
                    </td>
                    <td className="py-4 px-6 text-gray-400 max-w-xs truncate">
                      {voucher.narration || "-"}
                    </td>
                    <td className="py-4 px-6 text-right font-medium">
                      ₹{voucher.totalDebit || 0}
                    </td>
                    <td className="py-4 px-6 text-center">
                      <button
                        onClick={() => handleToggle(voucher._id)}
                        className="text-xl lg:text-2xl w-fit px-2 py-1 hover:opacity-80"
                        title={
                          voucher.isActive
                            ? "Cancel voucher"
                            : "Reactivate voucher"
                        }
                      >
                        <FontAwesomeIcon
                          icon={voucher.isActive ? faToggleOn : faToggleOff}
                          className={voucher.isActive ? "text-emerald-400" : "text-gray-500"}
                        />
                      </button>
                    </td>
                    <td className="py-4 px-6 text-center">
                      <div className="flex justify-center gap-3">
                        <button
                          onClick={() => handleDownloadPdf(voucher)}
                          className="text-lg lg:text-xl w-fit px-2 py-1 text-gray-400 hover:text-cyan-400"
                          title="Download PDF"
                        >
                          <FontAwesomeIcon icon={faFilePdf} />
                        </button>
                        <button
                          onClick={() => openEditModal(voucher)}
                          className="text-lg lg:text-xl w-fit px-2 py-1 text-gray-400 hover:text-cyan-400"
                        >
                          <FontAwesomeIcon icon={faEdit} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {filteredVouchers.length === 0 && (
            <div className="text-center py-16 text-gray-500">
              No vouchers found. Create your first voucher.
            </div>
          )}
        </div>
      </div>

      {/* Create/Edit Voucher Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-gray-800 rounded-3xl w-full max-w-4xl max-h-[95vh] flex flex-col">
            <div className="p-6 border-b border-gray-700 flex justify-between items-center">
              <h2 className="text-2xl font-bold text-cyan-200">
                {editingVoucher ? "Edit Voucher" : "New Voucher Entry"}
              </h2>
              <button
                onClick={() => setShowModal(false)}
                className="tetx-xl lg:text-2xl w-fit px-2 py-1 text-gray-400 hover:text-white"
              >
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>

            <form
              onSubmit={handleSubmit}
              className="flex-1 overflow-auto p-6 space-y-6"
            >
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-gray-400 mb-1">
                    Voucher Type
                  </label>
                  <select
                    value={formData.voucherType}
                    onChange={(e) =>
                      setFormData({ ...formData, voucherType: e.target.value })
                    }
                    className="w-full bg-gray-700 border border-gray-600 rounded-xl px-2 py-1"
                  >
                    {voucherTypes.map((type) => (
                      <option key={type} value={type}>
                        {type}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-gray-400 mb-1">
                    Voucher Number
                  </label>
                  <input
                    type="text"
                    value={formData.voucherNumber}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        voucherNumber: e.target.value,
                      })
                    }
                    placeholder="Auto-generated if empty"
                    className="w-full bg-gray-700 border border-gray-600 rounded-xl px-4 py-3"
                  />
                </div>

                <div>
                  <label className="block text-gray-400 mb-1">Date</label>
                  <input
                    type="date"
                    value={formData.date}
                    onChange={(e) =>
                      setFormData({ ...formData, date: e.target.value })
                    }
                    className="w-full bg-gray-700 border border-gray-600 rounded-xl px-4 py-3"
                  />
                </div>
              </div>

              {/* Dynamic Entries Table */}
              <div>
                <div className="flex justify-between items-center mb-3">
                  <h3 className="text-lg font-semibold text-yellow-100">
                    Double Entry Details
                  </h3>
                  <button
                    type="button"
                    onClick={addEntryRow}
                    className="w-fit px-2 py-1 text-cyan-400 hover:text-cyan-300 flex items-center gap-1 text-sm"
                  >
                    <FontAwesomeIcon icon={faPlus} /> Add Row
                  </button>
                </div>

                {/* Mobile Cards */}
                {/* <div className="lg:hidden space-y-3"> */}
                <div className="block lg:hidden space-y-3">
                  {formData.entries.map((entry, index) => (
                    <div
                      key={index}
                      className="bg-gray-900 border border-gray-700 rounded-xl p-3 space-y-3 text-xs"
                    >
                      <div>
                        <label className="block text-gray-400 mb-1">
                          Ledger
                        </label>
                        <select
                          value={entry.ledgerId}
                          onChange={(e) =>
                            updateEntry(index, "ledgerId", e.target.value)
                          }
                          className="w-full bg-gray-700 border border-gray-600 rounded-lg px-2 py-2"
                        >
                          <option value="">Select Ledger</option>
                          {ledgersList.map((ledger) => (
                            <option key={ledger._id} value={ledger._id}>
                              {ledger.ledgerCode} - {ledger.name}
                            </option>
                          ))}
                        </select>
                      </div>

                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <label className="block text-gray-400 mb-1">
                            Debit
                          </label>
                          <input
                            type="number"
                            value={entry.debit}
                            onChange={(e) =>
                              updateEntry(index, "debit", e.target.value)
                            }
                            className="w-full bg-gray-700 border border-gray-600 rounded-lg px-2 py-2 text-right"
                          />
                        </div>

                        <div>
                          <label className="block text-gray-400 mb-1">
                            Credit
                          </label>
                          <input
                            type="number"
                            value={entry.credit}
                            onChange={(e) =>
                              updateEntry(index, "credit", e.target.value)
                            }
                            className="w-full bg-gray-700 border border-gray-600 rounded-lg px-2 py-2 text-right"
                          />
                        </div>
                      </div>

                      <div className="flex justify-end">
                        <button
                          type="button"
                          onClick={() => removeEntryRow(index)}
                          className="w-fit px-1 py-1 text-red-400 hover:text-red-500"
                        >
                          <FontAwesomeIcon icon={faTrash} />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Desktop Table */}
                <div className="hidden lg:block bg-gray-900 rounded-2xl overflow-x-auto">
                {/* <div className="hidden lg:block bg-gray-900 rounded-2xl overflow-hidden"> */}
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-gray-700">
                        <th className="text-left py-3 px-3">Ledger</th>
                        <th className="text-right py-3 px-3">Debit (₹)</th>
                        <th className="text-right py-3 px-3">Credit (₹)</th>
                        <th className="text-center py-3 px-3">Action</th>
                      </tr>
                    </thead>

                    <tbody>
                      {formData.entries.map((entry, index) => (
                        <tr
                          key={index}
                          className="border-b border-gray-800 text-xs lg:text-sm"
                        >
                          <td className="py-2 px-3">
                            <select
                              value={entry.ledgerId}
                              onChange={(e) =>
                                updateEntry(index, "ledgerId", e.target.value)
                              }
                              className="w-full bg-gray-700 border border-gray-600 rounded-lg px-2 py-1 text-xs lg:text-sm"
                            >
                              <option value="">Select Ledger</option>
                              {ledgersList.map((ledger) => (
                                <option key={ledger._id} value={ledger._id}>
                                  {ledger.ledgerCode} - {ledger.name}
                                </option>
                              ))}
                            </select>
                          </td>

                          <td className="py-2 px-3">
                            <input
                              type="number"
                              value={entry.debit}
                              onChange={(e) =>
                                updateEntry(index, "debit", e.target.value)
                              }
                              className="w-full bg-gray-700 border border-gray-600 rounded-lg px-2 py-1 text-right text-xs lg:text-sm"
                            />
                          </td>

                          <td className="py-2 px-3">
                            <input
                              type="number"
                              value={entry.credit}
                              onChange={(e) =>
                                updateEntry(index, "credit", e.target.value)
                              }
                              className="w-full bg-gray-700 border border-gray-600 rounded-lg px-2 py-1 text-right text-xs lg:text-sm"
                            />
                          </td>

                          <td className="py-2 px-3 text-center">
                            <button
                              type="button"
                              onClick={() => removeEntryRow(index)}
                              className="w-fit px-1 py-1 text-red-400 hover:text-red-500"
                            >
                              <FontAwesomeIcon icon={faTrash} />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <div className="flex justify-end gap-8 mt-3 text-lg font-medium">
                  <div>
                    Total Debit:{" "}
                    <span className="text-red-400">₹{totalDebit}</span>
                  </div>
                  <div>
                    Total Credit:{" "}
                    <span className="text-green-400">₹{totalCredit}</span>
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-gray-400 mb-1">
                  Narration / Remarks
                </label>
                <textarea
                  value={formData.narration}
                  onChange={(e) =>
                    setFormData({ ...formData, narration: e.target.value })
                  }
                  style={{height: "100px"}}
                  className="w-full bg-gray-700 border border-gray-600 rounded-2xl px-4 py-3"
                  placeholder="Enter narration for this voucher..."
                />
              </div>

              <StatusBanner status={status} />

              <div className="flex gap-4 pt-4 border-t border-gray-700">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="flex-1 w-fit px-2 py-1 border border-gray-600 rounded-2xl hover:bg-gray-700 font-medium"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 w-fit px-2 py-1 bg-cyan-600 hover:bg-cyan-500 rounded-2xl font-medium flex items-center justify-center gap-2"
                >
                  <FontAwesomeIcon icon={faSave} />{" "}
                  {editingVoucher ? "Update Voucher" : "Save Voucher"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default MiniERPVoucher;